import { type ComponentType, createContext, type ReactNode, type SVGProps, useContext } from 'react';

import type { ToastAction, ToastPlacement, ToastVariant } from 'components/ui/shared/Toast/Toast';

export interface NotificationContextInterface {
  /**
   * Show an error notification whose message is extracted from an Apollo error
   * (CombinedGraphQLErrors → ServerError → Error.message → fallback). Use this
   * any time you have an unknown error object and want the server-side message.
   */
  showErrorNotification: (error: unknown, options?: ShowErrorNotificationOptions) => void;
  /**
   * Show a notification with a structured payload. Accepts a convenience
   * string shortcut for quick callers — treated as the message body.
   */
  showNotification: (input: NotificationInput | string) => void;
}

export interface NotificationInput {
  /**
   * Optional action button rendered on the toast (e.g. "Undo"). Clicking it dismisses the toast.
   */
  action?: ToastAction;
  /**
   * Message body. Can be a string or rich content (e.g. a localized node with a link).
   */
  children: ReactNode;
  /**
   * Overrides the provider's default auto-dismiss delay for this toast only, in milliseconds.
   */
  duration?: number;
  /**
   * Ties the toast to whatever raised it, e.g. the row it reports on. A later notification carrying the
   * same key supersedes this one instead of stacking on top of it.
   */
  groupKey?: string;
  /**
   * Override the variant's default icon.
   */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Which corner or edge midpoint this toast sits at. Defaults to 'top-center', where every toast sits.
   */
  placement?: ToastPlacement;
  /**
   * Optional headline rendered above the message.
   */
  title?: string;
  /**
   * Visual variant of the toast. Defaults to 'error' when omitted.
   */
  variant?: ToastVariant;
}

export interface ShowErrorNotificationOptions {
  /**
   * Ties the toast to whatever raised it, so a failure supersedes that source's earlier toast rather
   * than leaving a stale success beside it.
   */
  groupKey?: string;
  /**
   * Override the default error icon.
   */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Where this toast sits. Defaults to 'top-center', alongside every other toast.
   */
  placement?: ToastPlacement;
  /**
   * Optional headline rendered above the extracted error message.
   */
  title?: string;
}

/**
 * No-op default so components without the provider don't crash (e.g. isolated unit tests).
 */
const noOp = () => {};

export const NotificationContext = createContext<NotificationContextInterface>({
  showErrorNotification: noOp,
  showNotification: noOp,
});

export const useNotification = () => useContext(NotificationContext);
